// src/pages/Community/Weapons/WeponsVehicleForm.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import {
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { toast } from "react-toastify";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { GTA_GAME_TABS as GAMES } from "../components/ToolbarHeader";
import "./css/MissionsTab.css"; // keeps mt6-* styles

// ---------------- Controlled vocabs ----------------
const ASSET_TYPES = ["Weapon", "Vehicle"];
const SAMPLE_WEAPON = {
  type: "Weapon",

  // ---- Game association ----
  gameKey: "gta-5",
  game: "GTA V",
  gameMode: "story",
  isPrivate: false,

  // ---- Common fields ----
  name: "Fire Extinguisher",
  manufacturer: "Shrewsbury",
  price: "0",

  tagsCsv: "utility, non-lethal, fire suppression, story-mode, director-mode",

  coverFile: null,
  coverImage: "",
  coverUrl: "",
  gallery: [""],
  videoUrl: "",

  description:
    "**Fire Extinguisher** is a utility tool used to put out fires and cool overheated vehicles.\n\nWhile non-lethal, it’s useful for crowd control, extinguishing flames, and cinematic scenarios in Story Mode and Director Mode.",

  acquisition:
    "**How to get**\n- Automatically available during fire-related missions (Story Mode)\n- Commonly found near fire trucks and emergency scenes\n- Always available in Director Mode",

  // ---- Weapon-only fields ----
  w_category: "Special",
  w_ammoType: "None",
  w_magazineSize: "Unlimited",
  w_damage: "0",
  w_accuracy: "90",
  w_rangeMeters: "8",
  w_fireRateRpm: "0",
  w_reloadSec: "0",
  w_weightKg: "5.0",
  w_attachmentsCsv: "none",

  // ---- Vehicle-only fields (required by form state) ----
  v_class: "Sedans",
  v_seats: "",
  v_drivetrain: "FWD",
  v_topSpeedKph: "",
  v_accel0to100: "",
  v_brake100to0m: "",
  v_handling: "",
  v_armor: "",
  v_boostNitro: false,
  v_waterCapable: false,
  v_storage: "",
  v_spawnLocations: [""],

  // ---- Vehicle meta ----
  v_designInspiration: "",

  // ---- Vehicle performance stars ----
  v_perfTopSpeed: "",
  v_perfAcceleration: "",
  v_perfBraking: "",
  v_perfHandling: "",
  v_perfDurability: "",
  v_perfOffroad: "",
  v_perfUtility: ""
};

const SAMPLE_VEHICLE = {
  type: "Vehicle",

  // 🔹 Game
  gameKey: "gta-5",
  game: "GTA V",
  gameMode: "online",
  isPrivate: false,

  // 🔹 Common
  name: "Festival Bus",
  manufacturer: "Vapid",
  price: "1842000",
  tagsCsv: "service, party, bus, special, entertainment",

  coverFile: null,
  coverImage: "",
  coverUrl: "",
  gallery: [""],
  videoUrl: "",

  description:
    "**Festival Bus** is a rolling nightclub packed with speakers, lights, and DJ equipment.\nDesigned purely for chaos and celebration, it turns any street into a moving music festival.",

  acquisition:
    "**How to get**\n- Purchasable via Warstock Cache & Carry (Online)\n- Customizable with DJ and audio upgrades",

  // 🔹 VEHICLE FORM KEYS
  v_class: "Service",
  v_seats: "6",
  v_drivetrain: "RWD",

  v_topSpeedKph: "135",
  v_accel0to100: "11.8",
  v_brake100to0m: "58",

  v_handling: "40",
  v_armor: "50",

  v_boostNitro: false,
  v_waterCapable: false,

  v_storage: "DJ booth, speakers, lighting rigs",
  v_spawnLocations: [
    "Warstock Cache & Carry delivery (Online)",
    "Personal Vehicle spawn (Online)"
  ],

  // 🔹 Design Inspiration
  v_designInspiration: "Mobile festival and party buses",

  // 🔹 Performance Stars
  v_perfTopSpeed: "1",
  v_perfAcceleration: "1",
  v_perfBraking: "2",
  v_perfHandling: "1",
  v_perfDurability: "3",
  v_perfOffroad: "1",
  v_perfUtility: "5",
};

const WEAPON_CATEGORIES = [
  "Pistol",
  "SMG",
  "Assault Rifle",
  "Shotgun",
  "Sniper",
  "Melee",
  "Throwable",
  "Special",
  "Heavy",
];

const AMMO_TYPES = [
  "9mm",
  "5.56",
  "7.62",
  ".45",
  "12 Gauge",
  "Rockets",
  "None",
];

const GAME_MODES = [
  { key: "story", label: "GTA Story" },
  { key: "online", label: "GTA Online" },
];

// Rockstar-accurate vehicle classes
export const VEHICLE_CLASS_GROUPS = {
  "Performance": [
    "Super",
    "Sports",
    "Sports Classics",
    "Muscle",
  ],
  "Civilian": [
    "Sedans",
    "Coupes",
    "SUVs",
    "Compacts",
    "Vans",
  ],
  "Utility & Work": [
    "Utility",
    "Industrial",
    "Commercial",
    "Service",
  ],
  "Special": [
    "Off-Road",
    "Military",
    "Emergency",
  ],
  "Two-Wheel": [
    "Motorcycles",
  ],
  "Water & Air": [
    "Boats",
    "Helicopters",
    "Planes",
  ],
};

const DRIVETRAINS = [
  // Road
  "FWD",
  "RWD",
  "AWD",
  "4x4",

  // Non-road
  "N/A",        // planes, boats, trailers
  "Propeller", // planes
  "Jet",       // jets
  "Helicopter",// helicopters
  "Tracked",   // bulldozers, tanks
  "Waterjet",  // boats
];


// ---------- shared helpers ----------
const MAX_IMAGE_BYTES = 6 * 1024 * 1024; // 6 MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
export const VEHICLE_CLASSES = Object.values(VEHICLE_CLASS_GROUPS).flat();

export const DEFAULT_VEHICLE_CLASS = "Sedans";

const labelByKey = (k) => GAMES.find((g) => g.key === k)?.label || "Other";
const toSlug = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

// ---- Optional preset for quick testing ----
const ALEUTIAN_PRESET_FORM = {
  // game
  gameKey: "gta-6",
  game: "GTA VI",
  isPrivate: false,

  // Common
  name: "Vapid Aleutian",
  manufacturer: "Vapid",
  price: "95000",
  tagsCsv: "SUV, full-size, suburban, 8-seater, tow, family",
  coverFile: null,
  coverImage: "",
  coverUrl: "", // final resolved url shown during edit (optional)
  gallery: [""],
  videoUrl: "",
  description: `**Vapid Aleutian (SUV)**  
A king-sized suburban fortress with three rows of leather, wallowy cornering, and the sort of grille that scares compact cars at crosswalks. Built to tow twice a year and intimidate daily.`,
  acquisition: `**How to get**  
- Southern San Andreas Super Autos (online)  
- Premium Deluxe Motorsport (rotating)  
- Rare NPC spawn in affluent marina/valet areas  
- Trade price unlock after 5 towing contracts`,

  // Weapon-only (defaults kept blank)
  w_category: WEAPON_CATEGORIES[0],
  w_ammoType: AMMO_TYPES[0],
  w_magazineSize: "",
  w_damage: "",
  w_rangeMeters: "",
  w_fireRateRpm: "",
  w_accuracy: "",
  w_reloadSec: "",
  w_weightKg: "",
  w_attachmentsCsv: "",
  // Vehicle-only
  v_class: "SUVs",
  v_seats: "8",
  v_drivetrain: "4x4",
  v_topSpeedKph: "190",
  v_accel0to100: "8.9",
  v_brake100to0m: "42",
  v_handling: "42",
  v_armor: "70",
  v_boostNitro: false,
  v_waterCapable: false,
  v_storage: "8 seats + large cargo area; cosmetic roof rack",
  v_spawnLocations: [
    "Port Gellhorn – Marina parking",
    "Vinewood Hills – Gated communities",
    "Downtown – Valet stands (rare)",
  ],

  // NEW vehicle meta
  v_designInspiration: "Fourth-generation Ford Expedition",

  // NEW performance stars (1–5)
  v_perfTopSpeed: "3",
  v_perfAcceleration: "2",
  v_perfBraking: "2",
  v_perfHandling: "2",
  v_perfDurability: "4",
  v_perfOffroad: "3",
  v_perfUtility: "5",
};
// Legacy → Canonical mapping
export const VEHICLE_CLASS_MAP = {
  Bike: "Motorcycles",
  Motorcycle: "Motorcycles",
  Truck: "Commercial",
  Offroad: "Off-Road",
  SUV: "SUVs",
  Sedan: "Sedans",
  Coupe: "Coupes",
  Boat: "Boats",
  Helicopter: "Helicopters",
  Plane: "Planes",
  Service: "Service",
  Emergency: "Emergency",
};

export default function WeponsVehicleForm({
  user,
  role,
  onDone,
  onCancel,
  defaultGameKey = "gta-6",
}) {
  const isAuthed = Boolean(user?.uid);
  const roleStr = String(user?.role || role || "").toLowerCase();
  const isSuperAdmin = roleStr === "superadmin"; // still available for audit/flags if you want

  // Allow any signed-in user to create assets
  const canCreate = isAuthed;


  const storage = getStorage();

  const [assetType, setAssetType] = useState("Weapon");
  const [banner, setBanner] = useState({ type: null, text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState([]);
  const [previews, setPreviews] = useState({ description: false, acquisition: false });

  // ------------ shared form -------------
  const [form, setForm] = useState({
    // Game association + visibility (parity with LocationForm)
    gameKey: defaultGameKey,
    game: labelByKey(defaultGameKey),
    gameMode: "story",
    isPrivate: false,

    // Common
    name: "",
    manufacturer: "",
    price: "",
    tagsCsv: "",
    coverFile: null,     // upload (optional)
    coverImage: "",      // URL (optional)
    coverUrl: "",        // resolved after upload (optional)
    gallery: [""],       // URL list
    videoUrl: "",
    description: "",
    acquisition: "", // where to buy/unlock/find

    // Weapon-only
    w_category: WEAPON_CATEGORIES[0],
    w_ammoType: AMMO_TYPES[0],
    w_magazineSize: "",
    w_damage: "",
    w_rangeMeters: "",
    w_fireRateRpm: "",
    w_accuracy: "",
    w_reloadSec: "",
    w_weightKg: "",
    w_attachmentsCsv: "",

    // Vehicle-only
    v_class: DEFAULT_VEHICLE_CLASS,
    v_seats: "",
    v_drivetrain: DRIVETRAINS[0],
    v_topSpeedKph: "",
    v_accel0to100: "", // seconds
    v_brake100to0m: "",
    v_handling: "",
    v_armor: "",
    v_boostNitro: false,
    v_waterCapable: false,
    v_storage: "",
    v_spawnLocations: [""],

    // Vehicle meta
    v_designInspiration: "",

    // Vehicle performance stars (1–5)
    v_perfTopSpeed: "",
    v_perfAcceleration: "",
    v_perfBraking: "",
    v_perfHandling: "",
    v_perfDurability: "",
    v_perfOffroad: "",
    v_perfUtility: "",
  });

  // ----- field updaters (LocationForm style) -----
  const updateField = useCallback((k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
  }, []);

  const onChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    if (name === "coverFile") {
      const file = files?.[0] || null;
      if (file) {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          toast.error("Unsupported image type. Use JPG/PNG/WebP/GIF.");
          return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
          toast.error("Image too large. Max 6 MB.");
          return;
        }
      }
      setForm((f) => ({ ...f, coverFile: file }));
      return;
    }

    if (name === "gameKey") {
      setForm((f) => ({ ...f, gameKey: value, game: labelByKey(value) }));
      return;
    }

    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  const updateArrayField = (key, idx, value) =>
    setForm((s) => {
      const arr = [...s[key]];
      arr[idx] = value;
      return { ...s, [key]: arr };
    });

  const addArrayRow = (key, blank = "") =>
    setForm((s) => ({ ...s, [key]: [...s[key], blank] }));

  const removeArrayRow = (key, idx) =>
    setForm((s) => {
      const arr = s[key].filter((_, i) => i !== idx);
      return { ...s, [key]: arr.length ? arr : [""] };
    });

  const togglePreview = (key) =>
    setPreviews((p) => ({ ...p, [key]: !p[key] }));

  // ---------- validation ----------
  const issuesComputed = useMemo(() => {
    const errs = [];
    if (!isAuthed) errs.push("You must be signed in.");
    if (!form.name.trim()) errs.push("Name is required.");

    // URL checks
    const urlLike = (v) => {
      if (!v?.trim()) return true;
      try {
        // eslint-disable-next-line no-new
        new URL(v.trim());
        return true;
      } catch {
        return false;
      }
    };
    if (!["story", "online"].includes(form.gameMode)) {
      errs.push("Invalid game mode.");
    }
    if (form.coverImage && !urlLike(form.coverImage))
      errs.push("Cover Image URL is invalid.");
    if (form.videoUrl && !urlLike(form.videoUrl))
      errs.push("Video URL is invalid.");
    form.gallery.forEach((u, i) => {
      if (u.trim() && !urlLike(u)) errs.push(`Gallery URL #${i + 1} is invalid.`);
    });

    // cover file checks (redundant with onChange, but keeps parity)
    if (form.coverFile) {
      if (!ALLOWED_IMAGE_TYPES.includes(form.coverFile.type))
        errs.push("Cover upload type must be JPG/PNG/WebP/GIF.");
      if (form.coverFile.size > MAX_IMAGE_BYTES)
        errs.push("Cover upload exceeds 6 MB.");
    }

    // Numbers helper
    const mustBeNumberIfFilled = (label, val) => {
      if (String(val).trim() !== "" && Number.isNaN(Number(val))) {
        errs.push(`${label} must be a number.`);
      }
    };

    // Shared numbers
    mustBeNumberIfFilled("price", form.price);

    if (assetType === "Weapon") {
      if (!WEAPON_CATEGORIES.includes(form.w_category)) {
        errs.push("Weapon category is invalid.");
      }
      mustBeNumberIfFilled("magazineSize", form.w_magazineSize);
      mustBeNumberIfFilled("damage", form.w_damage);
      mustBeNumberIfFilled("range (m)", form.w_rangeMeters);
      mustBeNumberIfFilled("fire rate (rpm)", form.w_fireRateRpm);
      mustBeNumberIfFilled("accuracy", form.w_accuracy);
      mustBeNumberIfFilled("reload (sec)", form.w_reloadSec);
      mustBeNumberIfFilled("weight (kg)", form.w_weightKg);

      const pctFields = [
        ["damage", form.w_damage],
        ["accuracy", form.w_accuracy],
      ];
      pctFields.forEach(([label, val]) => {
        if (String(val).trim() === "") return;
        const n = Number(val);
        if (n < 0 || n > 100) errs.push(`${label} must be between 0 and 100.`);
      });
    } else {
      // Vehicle
      if (!VEHICLE_CLASSES.includes(form.v_class)) errs.push("Vehicle class is invalid.");
      if (!DRIVETRAINS.includes(form.v_drivetrain)) errs.push("Drivetrain is invalid.");

      mustBeNumberIfFilled("seats", form.v_seats);
      mustBeNumberIfFilled("top speed (kph)", form.v_topSpeedKph);
      mustBeNumberIfFilled("0–100 km/h (sec)", form.v_accel0to100);
      mustBeNumberIfFilled("braking (m)", form.v_brake100to0m);
      mustBeNumberIfFilled("handling", form.v_handling);
      mustBeNumberIfFilled("armor", form.v_armor);

      const pctFieldsV = [
        ["handling", form.v_handling],
        ["armor", form.v_armor],
      ];
      pctFieldsV.forEach(([label, val]) => {
        if (String(val).trim() === "") return;
        const n = Number(val);
        if (n < 0 || n > 100) errs.push(`${label} must be between 0 and 100.`);
      });

      const mustBeIntInRangeIfFilled = (label, val, min, max) => {
        const s = String(val).trim();
        if (s === "") return;
        const n = Number(s);
        if (!Number.isInteger(n) || n < min || n > max) {
          errs.push(`${label} must be an integer between ${min} and ${max}.`);
        }
      };

      mustBeIntInRangeIfFilled("Top Speed (stars)", form.v_perfTopSpeed, 1, 5);
      mustBeIntInRangeIfFilled("Acceleration (stars)", form.v_perfAcceleration, 1, 5);
      mustBeIntInRangeIfFilled("Braking (stars)", form.v_perfBraking, 1, 5);
      mustBeIntInRangeIfFilled("Handling (stars)", form.v_perfHandling, 1, 5);
      mustBeIntInRangeIfFilled("Durability (stars)", form.v_perfDurability, 1, 5);
      mustBeIntInRangeIfFilled("Off-road (stars)", form.v_perfOffroad, 1, 5);
      mustBeIntInRangeIfFilled("Utility (stars)", form.v_perfUtility, 1, 5);
    }

    return errs;
  }, [assetType, form, isAuthed, isSuperAdmin]);

  useEffect(() => setIssues(issuesComputed), [issuesComputed]);

  const canSubmit = issues.length === 0 && !submitting;

  // ---------- uploads ----------
  const uploadCoverIfAny = async () => {
    if (!form.coverFile || !user?.uid) {
      // prefer uploaded file, otherwise use typed URL (coverImage)
      const typed = form.coverImage?.trim() || "";
      return typed;
    }
    const file = form.coverFile;
    const safeName = file.name.replace(/[^\w.\-]+/g, "_");
    const path = `assets/${user.uid}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  // ---------- submit ----------
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setBanner({ type: null, text: "" });

    try {
      const resolvedCover = await uploadCoverIfAny();

      const common = {
        // association + visibility
        gameKey: form.gameKey,
        game: form.game,
        gameMode: form.gameMode,
        isPrivate: !!form.isPrivate,

        // core
        type: assetType,
        name: form.name.trim(),
        slug: toSlug(form.name),
        manufacturer: form.manufacturer.trim() || null,
        price: form.price === "" ? null : Number(form.price),

        // media/meta
        tags: form.tagsCsv
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        coverUrl: resolvedCover || null,
        gallery: Array.from(new Set(form.gallery.map((g) => g.trim()).filter(Boolean))),
        videoUrl: form.videoUrl.trim() || null,
        description: form.description.trim(),
        acquisition: form.acquisition.trim(),

        // audit
        upvotes: 0,
        pending: !isSuperAdmin,
        authorUid: user.uid,
        authorEmail: user.email || null,
        authorName: user.displayName || "Anonymous",
        roleAtCreation: roleStr || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      let payload = { ...common };
      let targetCollection = "assets"; // default bucket, but we split below
      const normalizedVehicleClass =
        VEHICLE_CLASS_MAP[form.v_class] || form.v_class;

      if (assetType === "Weapon") {
        payload = {
          ...common,
          category: form.w_category,
          ammoType: form.w_ammoType,
          magazineSize:
            form.w_magazineSize === "" ? null : Number(form.w_magazineSize),
          damage: form.w_damage === "" ? null : Number(form.w_damage),
          rangeMeters:
            form.w_rangeMeters === "" ? null : Number(form.w_rangeMeters),
          fireRateRpm:
            form.w_fireRateRpm === "" ? null : Number(form.w_fireRateRpm),
          accuracy: form.w_accuracy === "" ? null : Number(form.w_accuracy),
          reloadSec: form.w_reloadSec === "" ? null : Number(form.w_reloadSec),
          weightKg: form.w_weightKg === "" ? null : Number(form.w_weightKg),
          attachments: form.w_attachmentsCsv
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
        };
        targetCollection = "weapons";
      } else {
        payload = {
          ...common,
          class: normalizedVehicleClass,
          classOriginal:
            normalizedVehicleClass !== form.v_class ? form.v_class : null,

          seats: form.v_seats === "" ? null : Number(form.v_seats),
          drivetrain: form.v_drivetrain,
          topSpeedKph:
            form.v_topSpeedKph === "" ? null : Number(form.v_topSpeedKph),
          accel0to100:
            form.v_accel0to100 === "" ? null : Number(form.v_accel0to100),
          brake100to0m:
            form.v_brake100to0m === "" ? null : Number(form.v_brake100to0m),
          handling: form.v_handling === "" ? null : Number(form.v_handling),
          armor: form.v_armor === "" ? null : Number(form.v_armor),
          boostNitro: Boolean(form.v_boostNitro),
          waterCapable: Boolean(form.v_waterCapable),
          storage: form.v_storage.trim() || null,
          spawnLocations: form.v_spawnLocations.map((t) => t.trim()).filter(Boolean),

          // NEW fields
          designInspiration: form.v_designInspiration.trim() || null,
          performanceStats: {
            topSpeedStars: form.v_perfTopSpeed.trim()
              ? Number(form.v_perfTopSpeed)
              : null,
            accelerationStars: form.v_perfAcceleration.trim()
              ? Number(form.v_perfAcceleration)
              : null,
            brakingStars: form.v_perfBraking.trim()
              ? Number(form.v_perfBraking)
              : null,
            handlingStars: form.v_perfHandling.trim()
              ? Number(form.v_perfHandling)
              : null,
            durabilityStars: form.v_perfDurability.trim()
              ? Number(form.v_perfDurability)
              : null,
            offroadStars: form.v_perfOffroad.trim()
              ? Number(form.v_perfOffroad)
              : null,
            utilityStars: form.v_perfUtility.trim()
              ? Number(form.v_perfUtility)
              : null,
          },
        };
        targetCollection = "vehicles";
      }

      await addDoc(collection(db, targetCollection), payload);

      setBanner({ type: "ok", text: `${assetType} saved successfully.` });
      toast.success(`${assetType} saved successfully`);

      // Reset (keep game, assetType, permissions)
      setForm((s) => ({
        ...s,
        gameMode: s.gameMode,
        name: "",
        manufacturer: "",
        price: "",
        tagsCsv: "",
        coverFile: null,
        coverImage: "",
        coverUrl: "",
        gallery: [""],
        videoUrl: "",
        description: "",
        acquisition: "",
        w_category: WEAPON_CATEGORIES[0],
        w_ammoType: AMMO_TYPES[0],
        w_magazineSize: "",
        w_damage: "",
        w_rangeMeters: "",
        w_fireRateRpm: "",
        w_accuracy: "",
        w_reloadSec: "",
        w_weightKg: "",
        w_attachmentsCsv: "",
        v_class: DEFAULT_VEHICLE_CLASS,
        v_seats: "",
        v_drivetrain: DRIVETRAINS[0],
        v_topSpeedKph: "",
        v_accel0to100: "",
        v_brake100to0m: "",
        v_handling: "",
        v_armor: "",
        v_boostNitro: false,
        v_waterCapable: false,
        v_storage: "",
        v_spawnLocations: [""],
        v_designInspiration: "",
        v_perfTopSpeed: "",
        v_perfAcceleration: "",
        v_perfBraking: "",
        v_perfHandling: "",
        v_perfDurability: "",
        v_perfOffroad: "",
        v_perfUtility: "",
      }));
      setPreviews({ description: false, acquisition: false });
      onDone?.();
    } catch (err) {
      console.error("[assets:addDoc] error:", err);
      setBanner({ type: "error", text: "Save failed. Try again." });
      toast.error("Save failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // pills preview for tags
  const previewTags = useMemo(
    () =>
      form.tagsCsv
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    [form.tagsCsv]
  );

  // Quick-fill helper (kept from your version, now parity-reset)
  const quickFillAleutian = () => {
    if (!canCreate) return;
    loadSampleAsset({ type: "Vehicle", ...ALEUTIAN_PRESET_FORM });
  };

  const loadSampleAsset = (sample) => {
    setAssetType(sample.type);

    setForm((s) => ({
      ...s,

      // reset weapon fields
      ...(sample.type === "Weapon"
        ? {
          v_class: DEFAULT_VEHICLE_CLASS,
          v_seats: "",
          v_drivetrain: DRIVETRAINS[0],
          v_topSpeedKph: "",
          v_accel0to100: "",
          v_brake100to0m: "",
          v_handling: "",
          v_armor: "",
          v_boostNitro: false,
          v_waterCapable: false,
          v_storage: "",
          v_spawnLocations: [""],
          v_designInspiration: "",
          v_perfTopSpeed: "",
          v_perfAcceleration: "",
          v_perfBraking: "",
          v_perfHandling: "",
          v_perfDurability: "",
          v_perfOffroad: "",
          v_perfUtility: "",
        }
        : {
          w_category: WEAPON_CATEGORIES[0],
          w_ammoType: AMMO_TYPES[0],
          w_magazineSize: "",
          w_damage: "",
          w_rangeMeters: "",
          w_fireRateRpm: "",
          w_accuracy: "",
          w_reloadSec: "",
          w_weightKg: "",
          w_attachmentsCsv: "",
        }),

      // apply sample
      ...sample,

      // keep these
      gameKey: s.gameKey,
      game: s.game,
      isPrivate: s.isPrivate,

      // array safety
      gallery: sample.gallery?.length ? sample.gallery : [""],
      v_spawnLocations: sample.v_spawnLocations?.length
        ? sample.v_spawnLocations
        : [""],
    }));
  };

  return (
    <div className="mt6-wrap">
      {/* Banner */}
      {banner.type && (
        <div
          className={
            "mt6-banner " +
            (banner.type === "ok"
              ? "mt6-banner--ok"
              : banner.type === "warn"
                ? "mt6-banner--warn"
                : "mt6-banner--error")
          }
          role="status"
        >
          {banner.text}
        </div>
      )}

      {/* Issues */}
      {issues.length > 0 && (
        <ul className="mt6-issues" role="alert" aria-live="assertive">
          {issues.map((m, i) => (
            <li key={i}>• {m}</li>
          ))}
        </ul>
      )}

      <div className="mt6-card">
        <div className="mt6-header">
          <div>
            <h3 className="mt6-title">Add GTA Asset (Weapon / Vehicle)</h3>
            <p className="mt6-sub">
              Super Admin tool to insert official weapon & vehicle details with stats, media, and acquisition info.
            </p>
          </div>
        </div>

        {!isAuthed && (
          <div className="mt6-banner mt6-banner--warn">Please sign in.</div>
        )}
        <form onSubmit={onSubmit} className="mt6-form">
          {/* ---- Basics ---- */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Basics</h4>

            <div className="mt6-row-3">
              <div>
                <label className="mt6-label">Game*</label>
                <select
                  className="mt6-input"
                  name="gameKey"
                  value={form.gameKey}
                  onChange={onChange}
                  required
                  disabled={!canCreate || submitting}
                >
                  {GAMES.filter((g) => g.key !== "all").map(({ key, label }) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mt6-label">Mode*</label>
                <select
                  className="mt6-input"
                  name="gameMode"
                  value={form.gameMode}
                  onChange={onChange}
                  required
                  disabled={!canCreate || submitting}
                >
                  {GAME_MODES.map(({ key, label }) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

            </div>
            <div className="mt6-row-3">

              {/* Visibility */}
              <div>
                <label className="mt6-label">Visibility</label>
                <div className="mt6-segment" role="tablist" aria-label="Visibility">
                  <button
                    type="button"
                    className={`mt6-seg-btn ${!form.isPrivate ? "is-active" : ""}`}
                    onClick={() => updateField("isPrivate", false)}
                    aria-pressed={!form.isPrivate}
                    disabled={!canCreate || submitting}
                  >
                    Public
                  </button>
                  <button
                    type="button"
                    className={`mt6-seg-btn ${form.isPrivate ? "is-active" : ""}`}
                    onClick={() => updateField("isPrivate", true)}
                    aria-pressed={form.isPrivate}
                    disabled={!canCreate || submitting}
                  >
                    Private
                  </button>
                </div>
                <div className="mt6-muted" aria-live="polite" style={{ marginTop: 6 }}>
                  {form.isPrivate ? "Private: visible only to you." : "Public: visible to everyone."}
                </div>
              </div>

              {/* Asset Type */}
              <div>
                <label className="mt6-label">Asset Type</label>
                <div className="mt6-pills">
                  {ASSET_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`mt6-pill ${assetType === t ? "is-active" : ""}`}
                      onClick={() => setAssetType(t)}
                      disabled={!canCreate || submitting}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <label className="mt6-label" htmlFor="af-name">Name*</label>
            <input
              id="af-name"
              className="mt6-input"
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder={assetType === "Weapon" ? "e.g., VP9 Tactical" : "e.g., Pegassi Vortex"}
              required
              disabled={!canCreate || submitting}
            />

            <div className="mt6-row-3">
              <div>
                <label className="mt6-label">Manufacturer (optional)</label>
                <input
                  className="mt6-input"
                  name="manufacturer"
                  value={form.manufacturer}
                  onChange={onChange}
                  placeholder="e.g., Vom Feuer / Pegassi"
                  disabled={!canCreate || submitting}
                />
              </div>
              <div>
                <label className="mt6-label">Price (optional)</label>
                <input
                  className="mt6-input"
                  name="price"
                  value={form.price}
                  onChange={onChange}
                  placeholder="e.g., 150000"
                  disabled={!canCreate || submitting}
                />
              </div>
              <div>
                <label className="mt6-label">Tags (CSV)</label>
                <input
                  className="mt6-input"
                  name="tagsCsv"
                  value={form.tagsCsv}
                  onChange={onChange}
                  placeholder="fast, off-road / high-damage, stealth"
                  disabled={!canCreate || submitting}
                />
              </div>
            </div>

            {previewTags.length > 0 && (
              <div className="mt6-pills" style={{ marginTop: 8 }}>
                {previewTags.map((t) => (
                  <span key={t} className="mt6-pill is-active">#{t}</span>
                ))}
              </div>
            )}
          </div>

          {/* ---- Media ---- */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Media</h4>

            <div className="mt6-row-3">
              <div>
                <label className="mt6-label" htmlFor="af-cover-file">Cover Image (upload)</label>
                <input
                  id="af-cover-file"
                  type="file"
                  name="coverFile"
                  accept={ALLOWED_IMAGE_TYPES.join(",")}
                  onChange={onChange}
                  className="mt6-input"
                  disabled={!canCreate || submitting}
                />
              </div>

              <div>
                <label className="mt6-label" htmlFor="af-cover-url">Cover Image URL (optional)</label>
                <input
                  id="af-cover-url"
                  className="mt6-input"
                  name="coverImage"
                  value={form.coverImage}
                  onChange={onChange}
                  placeholder="https://…"
                  disabled={!canCreate || submitting}
                />
              </div>

              <div>
                <label className="mt6-label" htmlFor="af-video">Video URL (optional)</label>
                <input
                  id="af-video"
                  className="mt6-input"
                  name="videoUrl"
                  value={form.videoUrl}
                  onChange={onChange}
                  placeholder="https://youtube.com/…"
                  disabled={!canCreate || submitting}
                />
              </div>
            </div>

            <label className="mt6-label">Gallery URLs</label>
            {form.gallery.map((u, i) => (
              <div className="mt6-row" key={`g-${i}`}>
                <input
                  className="mt6-input"
                  value={u}
                  onChange={(e) => updateArrayField("gallery", i, e.target.value)}
                  placeholder={`Image URL #${i + 1}`}
                  disabled={!canCreate || submitting}
                />
                <button
                  type="button"
                  className="mt6-remove"
                  onClick={() => removeArrayRow("gallery", i)}
                  disabled={!canCreate || submitting || form.gallery.length <= 1}
                  aria-label={`Remove image URL ${i + 1}`}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="mt6-add"
              onClick={() => addArrayRow("gallery")}
              disabled={!canCreate || submitting}
            >
              ＋ Add Image
            </button>
          </div>

          {/* ---- Description ---- */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Description</h4>
            <div className="mt6-segment" role="tablist" aria-label="Description mode">
              <button
                type="button"
                className={`mt6-seg-btn ${!previews.description ? "is-active" : ""}`}
                onClick={() => togglePreview("description")}
                aria-pressed={!previews.description}
                disabled={submitting}
              >
                Write
              </button>
              <button
                type="button"
                className={`mt6-seg-btn ${previews.description ? "is-active" : ""}`}
                onClick={() => togglePreview("description")}
                aria-pressed={previews.description}
                disabled={submitting}
              >
                Preview
              </button>
            </div>

            {!previews.description ? (
              <textarea
                className="mt6-input mt6-input--textarea"
                name="description"
                value={form.description}
                onChange={onChange}
                rows={6}
                placeholder="Lore, characteristics, when to use… (Markdown supported)"
                disabled={!canCreate || submitting}
              />
            ) : (
              <div className="mt6-preview">
                {form.description.trim() ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                    {form.description}
                  </ReactMarkdown>
                ) : (
                  <span className="mt6-muted">Nothing to preview.</span>
                )}
              </div>
            )}
          </div>

          {/* ---- Acquisition ---- */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Acquisition / Unlock</h4>
            <div className="mt6-segment" role="tablist" aria-label="Acquisition mode">
              <button
                type="button"
                className={`mt6-seg-btn ${!previews.acquisition ? "is-active" : ""}`}
                onClick={() => togglePreview("acquisition")}
                aria-pressed={!previews.acquisition}
                disabled={submitting}
              >
                Write
              </button>
              <button
                type="button"
                className={`mt6-seg-btn ${previews.acquisition ? "is-active" : ""}`}
                onClick={() => togglePreview("acquisition")}
                aria-pressed={previews.acquisition}
                disabled={submitting}
              >
                Preview
              </button>
            </div>

            {!previews.acquisition ? (
              <textarea
                className="mt6-input mt6-input--textarea"
                name="acquisition"
                value={form.acquisition}
                onChange={onChange}
                rows={5}
                placeholder="Vendors, mission rewards, locations, requirements… (Markdown supported)"
                disabled={!canCreate || submitting}
              />
            ) : (
              <div className="mt6-preview">
                {form.acquisition.trim() ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                    {form.acquisition}
                  </ReactMarkdown>
                ) : (
                  <span className="mt6-muted">Nothing to preview.</span>
                )}
              </div>
            )}
          </div>

          {/* ---- Weapon fields ---- */}
          {assetType === "Weapon" && (
            <div className="mt6-section">
              <h4 className="mt6-h4">Weapon Stats</h4>
              <div className="mt6-row-3">
                <div>
                  <label className="mt6-label">Category</label>
                  <select
                    className="mt6-input"
                    name="w_category"
                    value={form.w_category}
                    onChange={onChange}
                    disabled={!canCreate || submitting}
                  >
                    {WEAPON_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mt6-label">Ammo Type</label>
                  <select
                    className="mt6-input"
                    name="w_ammoType"
                    value={form.w_ammoType}
                    onChange={onChange}
                    disabled={!canCreate || submitting}
                  >
                    {AMMO_TYPES.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mt6-label">Magazine Size</label>
                  <input
                    className="mt6-input"
                    name="w_magazineSize"
                    value={form.w_magazineSize}
                    onChange={onChange}
                    placeholder="e.g., 30"
                    disabled={!canCreate || submitting}
                  />
                </div>
              </div>

              <div className="mt6-row-3">
                <div>
                  <label className="mt6-label">Damage (0–100)</label>
                  <input
                    className="mt6-input"
                    name="w_damage"
                    value={form.w_damage}
                    onChange={onChange}
                    placeholder="e.g., 78"
                    disabled={!canCreate || submitting}
                  />
                </div>
                <div>
                  <label className="mt6-label">Accuracy (0–100)</label>
                  <input
                    className="mt6-input"
                    name="w_accuracy"
                    value={form.w_accuracy}
                    onChange={onChange}
                    placeholder="e.g., 84"
                    disabled={!canCreate || submitting}
                  />
                </div>
                <div>
                  <label className="mt6-label">Range (meters)</label>
                  <input
                    className="mt6-input"
                    name="w_rangeMeters"
                    value={form.w_rangeMeters}
                    onChange={onChange}
                    placeholder="e.g., 120"
                    disabled={!canCreate || submitting}
                  />
                </div>
              </div>

              <div className="mt6-row-3">
                <div>
                  <label className="mt6-label">Fire Rate (RPM)</label>
                  <input
                    className="mt6-input"
                    name="w_fireRateRpm"
                    value={form.w_fireRateRpm}
                    onChange={onChange}
                    placeholder="e.g., 750"
                    disabled={!canCreate || submitting}
                  />
                </div>
                <div>
                  <label className="mt6-label">Reload Time (sec)</label>
                  <input
                    className="mt6-input"
                    name="w_reloadSec"
                    value={form.w_reloadSec}
                    onChange={onChange}
                    placeholder="e.g., 2.3"
                    disabled={!canCreate || submitting}
                  />
                </div>
                <div>
                  <label className="mt6-label">Weight (kg)</label>
                  <input
                    className="mt6-input"
                    name="w_weightKg"
                    value={form.w_weightKg}
                    onChange={onChange}
                    placeholder="e.g., 3.5"
                    disabled={!canCreate || submitting}
                  />
                </div>
              </div>

              <label className="mt6-label">Attachments (CSV)</label>
              <input
                className="mt6-input"
                name="w_attachmentsCsv"
                value={form.w_attachmentsCsv}
                onChange={onChange}
                placeholder="scope, suppressor, laser"
                disabled={!canCreate || submitting}
              />
            </div>
          )}

          {/* ---- Vehicle fields ---- */}
          {assetType === "Vehicle" && (
            <div className="mt6-section">
              <h4 className="mt6-h4">Vehicle Specs</h4>
              <div className="mt6-row-3">
                <div>
                  <label className="mt6-label">Class</label>
                  <select
                    className="mt6-input"
                    name="v_class"
                    value={form.v_class}
                    onChange={onChange}
                    disabled={!canCreate || submitting}
                  >
                    {Object.entries(VEHICLE_CLASS_GROUPS).map(([group, classes]) => (
                      <optgroup key={group} label={group}>
                        {classes.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mt6-label">Seats</label>
                  <input
                    className="mt6-input"
                    name="v_seats"
                    value={form.v_seats}
                    onChange={onChange}
                    placeholder="e.g., 2"
                    disabled={!canCreate || submitting}
                  />
                </div>
                <div>
                  <label className="mt6-label">Drivetrain</label>
                  <select
                    className="mt6-input"
                    name="v_drivetrain"
                    value={form.v_drivetrain}
                    onChange={onChange}
                    disabled={!canCreate || submitting}
                  >
                    {DRIVETRAINS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt6-row-3">
                <div>
                  <label className="mt6-label">Top Speed (kph)</label>
                  <input
                    className="mt6-input"
                    name="v_topSpeedKph"
                    value={form.v_topSpeedKph}
                    onChange={onChange}
                    placeholder="e.g., 210"
                    disabled={!canCreate || submitting}
                  />
                </div>
                <div>
                  <label className="mt6-label">0–100 km/h (sec)</label>
                  <input
                    className="mt6-input"
                    name="v_accel0to100"
                    value={form.v_accel0to100}
                    onChange={onChange}
                    placeholder="e.g., 3.4"
                    disabled={!canCreate || submitting}
                  />
                </div>
                <div>
                  <label className="mt6-label">100→0 km/h Braking (m)</label>
                  <input
                    className="mt6-input"
                    name="v_brake100to0m"
                    value={form.v_brake100to0m}
                    onChange={onChange}
                    placeholder="e.g., 35"
                    disabled={!canCreate || submitting}
                  />
                </div>
              </div>

              <div className="mt6-row-3">
                <div>
                  <label className="mt6-label">Handling (0–100)</label>
                  <input
                    className="mt6-input"
                    name="v_handling"
                    value={form.v_handling}
                    onChange={onChange}
                    placeholder="e.g., 86"
                    disabled={!canCreate || submitting}
                  />
                </div>
                <div>
                  <label className="mt6-label">Armor (0–100)</label>
                  <input
                    className="mt6-input"
                    name="v_armor"
                    value={form.v_armor}
                    onChange={onChange}
                    placeholder="e.g., 40"
                    disabled={!canCreate || submitting}
                  />
                </div>
                <div>
                  <label className="mt6-label">Storage (text)</label>
                  <input
                    className="mt6-input"
                    name="v_storage"
                    value={form.v_storage}
                    onChange={onChange}
                    placeholder="e.g., 2 small duffel bags"
                    disabled={!canCreate || submitting}
                  />
                </div>
              </div>

              <div className="mt6-row-3">
                <div>
                  <label className="mt6-label">Boost / Nitro</label>
                  <div className="mt6-pills">
                    {[true, false].map((b) => (
                      <button
                        key={String(b)}
                        type="button"
                        className={`mt6-pill ${form.v_boostNitro === b ? "is-active" : ""}`}
                        onClick={() => updateField("v_boostNitro", b)}
                        disabled={!canCreate || submitting}
                      >
                        {b ? "Yes" : "No"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mt6-label">Water Capable</label>
                  <div className="mt6-pills">
                    {[true, false].map((b) => (
                      <button
                        key={String(b)}
                        type="button"
                        className={`mt6-pill ${form.v_waterCapable === b ? "is-active" : ""}`}
                        onClick={() => updateField("v_waterCapable", b)}
                        disabled={!canCreate || submitting}
                      >
                        {b ? "Yes" : "No"}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mt6-label">Spawn Locations</label>
                  {form.v_spawnLocations.map((t, i) => (
                    <div className="mt6-row" key={`sp-${i}`}>
                      <input
                        className="mt6-input"
                        value={t}
                        onChange={(e) => updateArrayField("v_spawnLocations", i, e.target.value)}
                        placeholder={`Location #${i + 1}`}
                        disabled={!canCreate || submitting}
                      />
                      <button
                        type="button"
                        className="mt6-remove"
                        onClick={() => removeArrayRow("v_spawnLocations", i)}
                        disabled={!canCreate || submitting || form.v_spawnLocations.length <= 1}
                        aria-label={`Remove spawn location ${i + 1}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="mt6-add"
                    onClick={() => addArrayRow("v_spawnLocations")}
                    disabled={!canCreate || submitting}
                  >
                    ＋ Add Spawn Location
                  </button>
                </div>
              </div>

              {/* NEW: Design Inspiration */}
              <div className="mt6-section">
                <h4 className="mt6-h4">Design Inspiration</h4>
                <input
                  className="mt6-input"
                  name="v_designInspiration"
                  value={form.v_designInspiration}
                  onChange={onChange}
                  placeholder='e.g., "Fourth-generation Ford Expedition"'
                  disabled={!canCreate || submitting}
                />
              </div>

              {/* NEW: Performance (1–5 stars) */}
              <div className="mt6-section">
                <h4 className="mt6-h4">Performance (Stars 1–5)</h4>

                <div className="mt6-row-3">
                  <div>
                    <label className="mt6-label">Top Speed ★</label>
                    <input
                      className="mt6-input"
                      name="v_perfTopSpeed"
                      value={form.v_perfTopSpeed}
                      onChange={onChange}
                      placeholder="e.g., 3"
                      disabled={!canCreate || submitting}
                      inputMode="numeric"
                    />
                  </div>
                  <div>
                    <label className="mt6-label">Acceleration ★</label>
                    <input
                      className="mt6-input"
                      name="v_perfAcceleration"
                      value={form.v_perfAcceleration}
                      onChange={onChange}
                      placeholder="e.g., 2"
                      disabled={!canCreate || submitting}
                      inputMode="numeric"
                    />
                  </div>
                  <div>
                    <label className="mt6-label">Braking ★</label>
                    <input
                      className="mt6-input"
                      name="v_perfBraking"
                      value={form.v_perfBraking}
                      onChange={onChange}
                      placeholder="e.g., 2"
                      disabled={!canCreate || submitting}
                      inputMode="numeric"
                    />
                  </div>
                </div>

                <div className="mt6-row-3">
                  <div>
                    <label className="mt6-label">Handling ★</label>
                    <input
                      className="mt6-input"
                      name="v_perfHandling"
                      value={form.v_perfHandling}
                      onChange={onChange}
                      placeholder="e.g., 2"
                      disabled={!canCreate || submitting}
                      inputMode="numeric"
                    />
                  </div>
                  <div>
                    <label className="mt6-label">Durability ★</label>
                    <input
                      className="mt6-input"
                      name="v_perfDurability"
                      value={form.v_perfDurability}
                      onChange={onChange}
                      placeholder="e.g., 4"
                      disabled={!canCreate || submitting}
                      inputMode="numeric"
                    />
                  </div>
                  <div>
                    <label className="mt6-label">Off-road ★</label>
                    <input
                      className="mt6-input"
                      name="v_perfOffroad"
                      value={form.v_perfOffroad}
                      onChange={onChange}
                      placeholder="e.g., 3"
                      disabled={!canCreate || submitting}
                      inputMode="numeric"
                    />
                  </div>
                </div>

                <div className="mt6-row-3">
                  <div>
                    <label className="mt6-label">Utility ★</label>
                    <input
                      className="mt6-input"
                      name="v_perfUtility"
                      value={form.v_perfUtility}
                      onChange={onChange}
                      placeholder="e.g., 5"
                      disabled={!canCreate || submitting}
                      inputMode="numeric"
                    />
                  </div>
                  <div />
                  <div />
                </div>

                <p className="mt6-muted" style={{ marginTop: 8 }}>
                  Tip: Leave blank to skip a metric. Use integers 1–5.
                </p>
              </div>
            </div>
          )}

          {/* ---- Preview ---- */}
          <div className="mt6-section">
            <h4 className="mt6-h4">Preview</h4>
            <div className="mt6-preview">
              <div className="mt6-muted">Asset</div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                {form.name || "—"} <span>• {labelByKey(form.gameKey)}</span> <span>• {assetType}</span>
              </div>

              {(form.manufacturer || form.price || form.isPrivate) && (
                <div style={{ marginTop: 6 }}>
                  {form.manufacturer && (
                    <div>
                      <strong>Manufacturer:</strong> {form.manufacturer}
                    </div>
                  )}
                  {form.price && (
                    <div>
                      <strong>Price:</strong> {form.price}
                    </div>
                  )}
                  <div>
                    <strong>Visibility:</strong> {form.isPrivate ? "Private" : "Public"}
                  </div>
                </div>
              )}

              {previewTags.length > 0 && (
                <div className="mt6-pills" style={{ marginTop: 8 }}>
                  {previewTags.map((t) => (
                    <span key={t} className="mt6-pill is-active">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ---- Actions ---- */}
          <div className="mt6-actions">
            <button
              className="mt6-btn mt6-btn--primary"
              type="submit"
              disabled={!canSubmit}
            >
              {submitting ? "Submitting..." : `Save ${assetType}`}
            </button>

            {onCancel && (
              <button
                type="button"
                className="mt6-btn mt6-btn--ghost"
                onClick={onCancel}
                disabled={submitting}
              >
                Cancel
              </button>
            )}

            {isSuperAdmin && (
              <>
                <button
                  type="button"
                  className="mt6-btn mt6-btn--ghost"
                  onClick={() => loadSampleAsset(SAMPLE_WEAPON)}
                  disabled={submitting}
                >
                  ⚡ Load Sample Weapon
                </button>

                <button
                  type="button"
                  className="mt6-btn mt6-btn--ghost"
                  onClick={() => loadSampleAsset(SAMPLE_VEHICLE)}
                  disabled={submitting}
                >
                  ⚡ Load Sample Vehicle
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
